import { MODULE_CATALOGUE, screensFor, type ModuleCode } from '@his/shared';
import { tourHref } from '@/features/public/home/tour/tour-link';
import { TOUR_SCENES } from '@/features/public/home/tour/tour-scenes';
import type { TourScene } from '@/features/public/home/tour/tour-types';
import { NAV_ITEMS, allowsPath, longestPrefixMatch, permitsPath, type NavItem } from '@/lib/navigation';
import { findHelpEntry, findHelpEntryByKey, type HelpEntry } from './help-registry';
import { helpMessage } from './help-messages';

export type HelpLookup = (key: string) => string | undefined;

export interface HelpTopic {
  key: string;
  route: string;
  tab?: string;
  explicit: boolean;
  title: string;
  body: string;
  steps: string[];
  modules: ModuleCode[];
  tourSceneId?: string;
  links: string[];
  navLabel?: string;
}

export interface HelpScreenLink {
  to: string;
  label: string;
}

export interface HelpTourLink {
  sceneId: string;
  heading: string;
  href: string;
}

const MAX_CONNECTED = 8;

export function modulesOfItem(item: NavItem | undefined): ModuleCode[] {
  if (!item) return [];
  return item.anyModule ?? (item.module ? [item.module] : []);
}

export function navItemForPath(pathname: string): NavItem | undefined {
  return longestPrefixMatch(NAV_ITEMS, pathname);
}

function copyOf(key: string, lookup: HelpLookup): { title: string; body: string; steps: string[] } {
  const title = lookup(`help.${key}.title`)?.trim() ?? '';
  const body = lookup(`help.${key}.body`)?.trim() ?? '';
  const steps = (lookup(`help.${key}.steps`) ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return { title, body, steps };
}

function moduleDescription(modules: ModuleCode[]): string {
  const descriptions = modules
    .map((code) => MODULE_CATALOGUE.find((module) => module.code === code)?.description)
    .filter((text): text is string => Boolean(text));
  return [...new Set(descriptions)].slice(0, 2).join(' ');
}

function topicFromEntry(entry: HelpEntry | undefined, item: NavItem | undefined, route: string, lookup: HelpLookup): HelpTopic | null {
  if (!entry && !item) return null;
  const modules = modulesOfItem(item);
  const key = entry?.key ?? route.replace(/^\//, '').replace(/\//g, '.');
  const copy = entry ? copyOf(entry.key, lookup) : { title: '', body: '', steps: [] };
  const explicit = Boolean(entry) && copy.title.length > 0 && copy.body.length > 0 && copy.steps.length > 0;
  return {
    key,
    route,
    ...(entry?.tab ? { tab: entry.tab } : {}),
    explicit,
    title: copy.title || item?.label || '',
    body: copy.body || moduleDescription(modules),
    steps: copy.steps,
    modules,
    ...(entry?.tourSceneId ? { tourSceneId: entry.tourSceneId } : {}),
    links: entry?.links ?? [],
    ...(item ? { navLabel: item.label } : {}),
  };
}

export function helpFor(pathname: string, tab?: string, lookup: HelpLookup = helpMessage): HelpTopic | null {
  const item = navItemForPath(pathname);
  const route = item?.to ?? pathname;
  const entry = (tab ? findHelpEntry(route, tab) : undefined) ?? findHelpEntry(route);
  return topicFromEntry(entry, item, route, lookup);
}

export function helpByKey(key: string, lookup: HelpLookup = helpMessage): HelpTopic | null {
  const entry = findHelpEntryByKey(key);
  if (!entry) return null;
  return topicFromEntry(entry, navItemForPath(entry.route), entry.route, lookup);
}

export function hasTabHelp(pathname: string, tab: string): boolean {
  const item = navItemForPath(pathname);
  return Boolean(findHelpEntry(item?.to ?? pathname, tab));
}

export function connectedScreens(
  topic: HelpTopic,
  can: (...permissions: string[]) => boolean,
  hasModule: (module: ModuleCode) => boolean,
  isRetired: (module: ModuleCode) => boolean = () => false,
  label: (item: NavItem) => string = (item) => item.label,
): HelpScreenLink[] {
  const candidates = [
    ...topic.links,
    ...NAV_ITEMS.filter((item) => modulesOfItem(item).some((module) => topic.modules.includes(module))).map((item) => item.to),
    ...screensFor(topic.modules),
  ];
  const out: HelpScreenLink[] = [];
  const seen = new Set<string>([topic.route]);
  for (const candidate of candidates) {
    const item = navItemForPath(candidate);
    if (!item || seen.has(item.to)) continue;
    seen.add(item.to);
    if (!permitsPath(item.to, can) || !allowsPath(item.to, hasModule, isRetired)) continue;
    out.push({ to: item.to, label: label(item) });
    if (out.length >= MAX_CONNECTED) break;
  }
  return out;
}

export function tourSceneFor(topic: HelpTopic): TourScene | undefined {
  if (topic.tourSceneId) return TOUR_SCENES.find((scene) => scene.id === topic.tourSceneId);
  return TOUR_SCENES.find((scene) => scene.route !== '/login' && navItemForPath(scene.route)?.to === topic.route);
}

export function tourLinkFor(topic: HelpTopic): HelpTourLink | undefined {
  const scene = tourSceneFor(topic);
  if (!scene) return undefined;
  return { sceneId: scene.id, heading: scene.caption.heading, href: tourHref(scene.id) };
}
